// The file path into the workspace: POST /workspace/files on the app's
// own origin, multipart, one part per file under the name "file". The
// pages token rides as Bearer, as the Connect calls in rpc.ts carry it.
// A refusal arrives as its status; the Live screen words 413 and 409.
import { token } from "@/auth";
import { t } from "@/i18n";

export type Uploaded = { id: string; name: string; path: string; bytes: number };

export class UploadError extends Error {
  constructor(readonly status: number) {
    super(`upload refused: ${status}`);
  }
}

const SHOWN = 5;

// The line after an upload: the count, the first names, the rest as a
// number, so a batch of twenty stays one line.
export function uploadSummary(files: Uploaded[]): string {
  if (files.length === 0) return "";
  const shown = files.slice(0, SHOWN).map((f) => f.name);
  const rest = files.length - shown.length;
  const names = shown.join(", ") + (rest > 0 ? ` +${rest}` : "");
  const key = files.length === 1 ? "live.upload.one" : "live.upload.many";
  return t(key, "")
    .replace("{n}", () => String(files.length))
    .replace("{names}", () => names);
}

export async function upload(files: File[]): Promise<Uploaded[]> {
  const body = new FormData();
  for (const f of files) body.append("file", f, f.name);
  const headers: Record<string, string> = {};
  const t = token();
  if (t) headers.Authorization = `Bearer ${t.access}`;
  const res = await fetch(`${location.origin}/workspace/files`, { method: "POST", headers, body });
  if (res.status !== 201) throw new UploadError(res.status);
  const json = (await res.json()) as { files?: Uploaded[] };
  return json.files ?? [];
}
